import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { SearchOutlined } from "@ant-design/icons";

import FilterRenderer from "@/components_v2/Filters/FilterRenderer";
import { FilterField } from "@/components_v2/Filters/types";
import {
  HookFormBaseDropdown,
  HookFormInput,
} from "@/components_v2/FormFields/ReactHookForm/fields";
import { useFilterCount } from "@/shared/hooks/useFilterCount";
import type { CertificationInvitationStatus } from "@/redux/certifications/types";
import {
  INVITATION_STATUSES,
  STATUS_CHIPS,
} from "@/domains/certifications/constants";
import {
  InvitationFiltersProps,
  InvitationFiltersState,
} from "@/features/certifications/invitations/types";

const FILTER_NAMES = ["search", "status"];
const DEFAULT_FILTERS: InvitationFiltersState = {
  search: undefined,
  status: undefined,
};
const STATUS_VALUES = Object.keys(
  INVITATION_STATUSES,
) as CertificationInvitationStatus[];

const invitationFiltersSchema: yup.ObjectSchema<InvitationFiltersState> = yup
  .object({
    search: yup
      .string()
      .transform((value) => (value === "" ? undefined : value)),
    status: yup.mixed<CertificationInvitationStatus>().oneOf(STATUS_VALUES),
  })
  .required();

const InvitationFilters = ({ setFilters }: InvitationFiltersProps) => {
  const { t } = useTranslation();

  const form = useForm<InvitationFiltersState>({
    resolver: yupResolver(invitationFiltersSchema),
    mode: "onChange",
    defaultValues: DEFAULT_FILTERS,
  });

  const statusOptions = useMemo(
    () =>
      STATUS_VALUES.map((value) => ({
        value,
        label: t(STATUS_CHIPS[INVITATION_STATUSES[value]].labelKey),
      })),
    [t],
  );

  const filterFields: Record<string, FilterField> = useMemo(
    () => ({
      search: {
        name: "search",
        isAdvancedFilter: false,
        isDebounced: true,
        component: (
          <HookFormInput
            name="search"
            control={form.control}
            suffix={<SearchOutlined />}
            placeholder={t("certification.admin.invitations.searchPlaceholder")}
          />
        ),
      },
      status: {
        name: "status",
        label: t("certification.admin.invitations.status"),
        isAdvancedFilter: true,
        isDebounced: false,
        component: (
          <HookFormBaseDropdown
            name="status"
            control={form.control}
            options={statusOptions}
            placeholder={t("certification.admin.invitations.selectStatus")}
            allowClear
          />
        ),
      },
    }),
    [form.control, t, statusOptions],
  );

  const activeFilterCount = useFilterCount<InvitationFiltersState>(
    form,
    DEFAULT_FILTERS,
  );

  return (
    <FilterRenderer
      form={form}
      filters={filterFields}
      activeFilterNames={FILTER_NAMES}
      popoverTitle={t("certification.admin.invitations.filters")}
      handleClear={() => {
        form.reset(DEFAULT_FILTERS);
        setFilters(form.getValues());
      }}
      onFiltersChange={setFilters}
      areFiltersDirty={form.formState.isDirty}
      activeFilterCount={activeFilterCount}
      handleSearch={() => setFilters(form.getValues())}
      className="filter-layout"
    />
  );
};

export default InvitationFilters;
